import { computeOffset, estimateCount, feedPubkey, mergeHll, newHll } from '@/lib/hll'
import { tagNameEquals } from '@/lib/tag'
import client from '@/services/client.service'
import { loadRelayList } from '@nostr/gadgets/lists'
import { pool } from '@nostr/gadgets/global'
import * as kinds from '@nostr/tools/kinds'
import { hllDecode } from '@nostr/tools/nip45'
import LRUCache from '@fiatjaf/lru-cache/lru-cache'
import { atom, getDefaultStore } from 'jotai'
import { atomFamily } from 'jotai-family'
import { currentPubkeyAtom } from './session'
import { relayListAtom } from './lists'
import { NostrEvent } from '@nostr/tools/core'
import { observe } from 'jotai-effect'

export type NoteStatEntry = {
  offset: number
  hlls: {
    reactions: Uint8Array
    reposts: Uint8Array
    quotes: Uint8Array
    replies: Uint8Array
    comments: Uint8Array
  }
  counts: {
    reactions: number
    reposts: number
    quotes: number
    replies: number
    comments: number
  }
  reactionCount: number
  repostCount: number
  quoteCount: number
  replyCount: number
  didLike: boolean
  hasReposted: boolean
  hasReplied: boolean
  updatedAt?: number
}

type StatCategory = keyof NoteStatEntry['hlls']

export const noteStats = new Map<string, NoteStatEntry>()

const store = getDefaultStore()

const hasCalledCount = new LRUCache<string, number>(500)

export function shouldQueryCounts(id: string): boolean {
  const ts = hasCalledCount.get(id)
  if (ts && Date.now() - ts < 30000) return false
  hasCalledCount.set(id, Date.now())
  return true
}

export const reactionCount = atomFamily((id: string) => atom(getStats(id).reactionCount))
export const repostCount = atomFamily((id: string) => atom(getStats(id).repostCount))
export const quoteCount = atomFamily((id: string) => atom(getStats(id).quoteCount))
export const replyCount = atomFamily((id: string) => atom(getStats(id).replyCount))

export const didLike = atomFamily((id: string) => atom(getStats(id).didLike))
export const hasReposted = atomFamily((id: string) => atom(getStats(id).hasReposted))
export const hasReplied = atomFamily((id: string) => atom(getStats(id).hasReplied))

export function getStats(id: string): NoteStatEntry {
  const existing = noteStats.get(id)
  if (existing) return existing

  const entry = emptyEntry(id)
  noteStats.set(id, entry)
  return entry
}

export function ingestEvents(events: NostrEvent[]) {
  const currentPubkey = store.get(currentPubkeyAtom)

  for (const evt of events) {
    const targets = getEventTargets(evt)
    if (!targets) continue

    const [ids, category] = targets
    for (const id of ids) {
      mutateEntry(id, (entry) => {
        entry.hlls[category] = feedPubkey(entry.hlls[category], evt.pubkey, entry.offset)

        if (currentPubkey && evt.pubkey === currentPubkey) {
          if (category === 'reactions') entry.didLike = true
          if (category === 'reposts') entry.hasReposted = true
          if (category === 'replies' || category === 'comments') entry.hasReplied = true
        }
      })
    }
  }
}

observe((get) => {
  const currentPubkey = get(currentPubkeyAtom)
  if (!currentPubkey) return

  for (const id of noteStats.keys()) {
    mutateEntry(id, (entry) => {
      entry.didLike = false
      entry.hasReposted = false
      entry.hasReplied = false
    })
  }

  store.get(relayListAtom).then(async (relays) => {
    const events = await client.fetchEvents(
      relays
        .filter((r) => r.write)
        .map((r) => r.url)
        .slice(0, 3),
      {
        authors: [currentPubkey],
        kinds: [kinds.Reaction, kinds.Repost, kinds.GenericRepost, kinds.ShortTextNote],
        limit: 130
      }
    )
    ingestEvents(events)
  })
})

function updateAtoms(id: string) {
  const entry = getStats(id)
  entry.reactionCount = Math.max(estimateCount(entry.hlls.reactions), entry.counts.reactions)
  entry.repostCount = Math.max(estimateCount(entry.hlls.reposts), entry.counts.reposts)
  entry.quoteCount = Math.max(estimateCount(entry.hlls.quotes), entry.counts.quotes)
  entry.replyCount = Math.max(
    estimateCount(entry.hlls.replies) + estimateCount(entry.hlls.comments),
    entry.counts.replies + entry.counts.comments
  )

  store.set(reactionCount(id), entry.reactionCount)
  store.set(repostCount(id), entry.repostCount)
  store.set(quoteCount(id), entry.quoteCount)
  store.set(replyCount(id), entry.replyCount)
  store.set(didLike(id), entry.didLike)
  store.set(hasReplied(id), entry.hasReplied)
}

function mutateEntry(id: string, mutator: (entry: NoteStatEntry) => void) {
  const entry = getStats(id)
  mutator(entry)
  updateAtoms(id)
}

export async function fetchCounts(event: NostrEvent) {
  if (!shouldQueryCounts(event.id)) return

  const rl = await loadRelayList(event.pubkey)
  const relays = rl.items
    .filter((r) => r.read)
    .map((r) => r.url)
    .slice(0, 3)
  if (relays.length === 0) return

  const directives = ['reactions', 'reposts', 'quotes', 'replies', 'comments'] as const
  let ingested = false

  await Promise.all(
    directives.map(async (dir) => {
      try {
        const result = await pool.countMany(relays, event.id, dir)
        if (result.count > 0) {
          ingestCountResult(event.id, dir, result.count, result.hll)
          ingested = true
        }
      } catch (err) {
        // ignore
        console.warn('countMany(' + event.id + ') error:', err, relays)
      }
    })
  )

  if (ingested) updateAtoms(event.id)
}

export function ingestCountResult(id: string, directive: string, count: number, hllHex?: string) {
  const entry = getStats(id)
  if (!(directive in entry.counts)) return
  mutateEntry(id, (e) => {
    e.counts[directive as keyof typeof e.counts] = count
    if (hllHex) {
      const hll = hllDecode(hllHex)
      if (hll && directive in e.hlls)
        e.hlls[directive as keyof typeof e.hlls] = mergeHll(
          e.hlls[directive as keyof typeof e.hlls],
          hll
        )
    }
  })
}

function emptyEntry(id: string): NoteStatEntry {
  return {
    offset: computeOffset(id),
    hlls: {
      reactions: newHll(),
      reposts: newHll(),
      quotes: newHll(),
      replies: newHll(),
      comments: newHll()
    },
    counts: {
      reactions: 0,
      reposts: 0,
      quotes: 0,
      replies: 0,
      comments: 0
    },
    reactionCount: 0,
    repostCount: 0,
    quoteCount: 0,
    replyCount: 0,
    didLike: false,
    hasReposted: false,
    hasReplied: false
  }
}

function getEventTargets(evt: NostrEvent): [string[], StatCategory] | null {
  if (evt.kind === kinds.Reaction) {
    const ids = evt.tags.filter((t) => t[0] === 'e' || t[0] === 'a').map((t) => t[1])
    if (ids.length) return [ids, 'reactions']
  }

  if (evt.kind === kinds.Repost || evt.kind === kinds.GenericRepost) {
    const ids = evt.tags.filter((t) => t[0] === 'e' || t[0] === 'a').map((t) => t[1])
    if (ids.length) return [ids, 'reposts']
  }

  if (
    evt.kind === kinds.ShortTextNote ||
    evt.kind === kinds.Comment ||
    evt.kind === kinds.VoiceComment
  ) {
    const ids = evt.tags.filter(tagNameEquals('q')).map((t) => t[1])
    if (ids.length) return [ids, 'quotes']
  }

  if (evt.kind === kinds.ShortTextNote) {
    const replyTags = evt.tags.filter((t) => t[0] === 'e' && t[3] === 'reply').map((t) => t[1])
    const eTags = evt.tags.filter(tagNameEquals('e')).map((t) => t[1])
    const ids = replyTags.length ? replyTags : eTags
    if (ids.length) return [ids, 'replies']
  }

  if (evt.kind === kinds.Comment) {
    const ids = evt.tags.filter((t) => t[0] === 'E' || t[0] === 'e').map((t) => t[1])
    if (ids.length) return [ids, 'comments']
  }

  return null
}
